<template>
  <modal :open="internalOpen" :width="1000" data-cy="merge-pain-files-modal">
    <template #header>
      <modal-header variant="cross" @cancel="closeModal()">
        <h1>{{ $t('toolbar.export.mergePainFiles.modalTitle') }}</h1>
      </modal-header>
    </template>

    <div class="mt-8">
      <p class="text-table-text-gray mb-6">
        {{ $t('toolbar.export.mergePainFiles.description') }}
      </p>

      <div data-cy="merge-pain-files-modal-files-input" class="files-area mb-6">
        <Files
          v-model="uploads"
          accepted-files=".xml"
          multiple
          with-button
          :button-label="$t('toolbar.export.mergePainFiles.uploadFiles')"
        />
      </div>

      <busy-area :busy="inspecting" class="preview min-h-[6rem]">
        <!-- Hard blocks -->
        <div
          v-if="preview?.mixedAccounts"
          data-cy="merge-pain-files-modal-mixed-error"
          class="error-box"
        >
          {{ $t('toolbar.export.mergePainFiles.mixedAccounts') }}
        </div>
        <div
          v-else-if="preview && preview.invalidFiles.length > 0"
          data-cy="merge-pain-files-modal-invalid-error"
          class="error-box"
        >
          {{ $t('toolbar.export.mergePainFiles.invalidFiles', { files: preview.invalidFiles.join(', ') }) }}
        </div>

        <!-- Summary + target bank -->
        <template v-else-if="preview && preview.transactionCount > 0">
          <div class="summary-row">
            <span class="summary-label">{{ $t('toolbar.export.mergePainFiles.targetBank') }}</span>
            <span class="summary-value" data-cy="merge-pain-files-modal-target-bank">
              {{ targetBankLabel }}
            </span>
          </div>
          <div class="summary-text" data-cy="merge-pain-files-modal-summary">
            {{ $t('toolbar.export.mergePainFiles.summary', {
              files: preview.fileCount,
              transactions: preview.transactionCount,
              amount: formattedTotal,
            }) }}
          </div>

          <div
            v-if="preview.duplicateWarnings.length > 0"
            class="warning-box"
            data-cy="merge-pain-files-modal-duplicates"
          >
            <div class="warning-title">
              {{ $t('toolbar.export.mergePainFiles.duplicatesTitle') }}
            </div>
            <ul>
              <li v-for="(warning, i) in preview.duplicateWarnings" :key="i">
                {{ warning }}
              </li>
            </ul>
          </div>
        </template>

        <div v-else-if="uploads.length < 2" class="text-table-text-gray">
          {{ $t('toolbar.export.mergePainFiles.minFiles') }}
        </div>
      </busy-area>

      <div class="w-full flex justify-end gap-4 mt-10">
        <custom-button variant="secondary" @click="closeModal()">
          {{ $t('abort') }}
        </custom-button>
        <custom-button
          variant="success"
          data-cy="merge-pain-files-modal-combine-btn"
          :busy="merging"
          :disabled="!canCombine"
          @click="combine"
        >
          {{ $t('toolbar.export.mergePainFiles.combine') }}
        </custom-button>
      </div>
    </div>
  </modal>
</template>

<script lang="ts" setup>
import { computed, ref, watch } from 'vue';
import { useVModel } from '@vueuse/core';
import { useI18n } from 'vue-i18n';
import axios from 'axios';
import Modal from '@utils/Modal/Modal.vue';
import ModalHeader from '@utils/Modal/ModalHeader.vue';
import CustomButton from '@utils/CustomButton.vue';
import Files from '@components/utils/inputs/Files.vue';
import BusyArea from '@components/utils/busy/BusyArea.vue';
import { useNotify } from '@utils/notifications';
import { useDownloads } from '@hooks/tasks';
import { apiUrl } from '@hooks/fetch';
import { getAuthToken } from '@/utils/tokenManager';
import type { FileUpload } from '@components/utils/upload/fileUpload';
import type { Download, WbFile } from '@/entities/Entities';
import { formatNumberForCurrency } from '@/utils/helpers';

interface PainMergePreview {
  targetBankAccount: string | null;
  targetBankName: string | null;
  targetIban: string | null;
  fileCount: number;
  transactionCount: number;
  totalAmount: number;
  duplicateWarnings: string[];
  invalidFiles: string[];
  mixedAccounts: boolean;
  canMerge: boolean;
}

interface Props {
  open: boolean;
}

const props = defineProps<Props>();
const emit = defineEmits(['update:open']);
const internalOpen = useVModel(props, 'open', emit);

const { t } = useI18n();
const notify = useNotify();
const downloads = useDownloads();

const uploads = ref<FileUpload<WbFile>[]>([]);
const preview = ref<PainMergePreview | null>(null);
const inspecting = ref(false);
const merging = ref(false);

// Raw File objects the operator picked (kept client-side; never uploaded as standalone File entities).
const rawFiles = computed(() =>
  uploads.value.filter((u) => u.isNew).map((u) => (u as Extract<FileUpload<WbFile>, { isNew: true }>).file));

const targetBankLabel = computed(() => {
  if (!preview.value) return '—';
  if (preview.value.targetBankName) {
    return t(`toolbar.export.downloadPainFile.dropdown.${preview.value.targetBankName}`, preview.value.targetBankName);
  }
  return preview.value.targetIban ?? '—';
});

const formattedTotal = computed(() =>
  `${formatNumberForCurrency(preview.value?.totalAmount ?? 0)} CHF`);

// The preview (inspect) is a convenience that surfaces the target bank + warnings; the merge
// endpoint validates authoritatively. So enable Combine as soon as there are >= 2 files and there
// is no *known* block — don't keep it disabled just because the preview is slow / unavailable.
const hasKnownBlock = computed(() =>
  !!preview.value && (preview.value.mixedAccounts || preview.value.invalidFiles.length > 0));

const canCombine = computed(() =>
  rawFiles.value.length >= 2 && !merging.value && !hasKnownBlock.value);

const authHeaders = () => ({
  authorization: `Bearer ${getAuthToken()}`,
  accept: 'application/ld+json',
});

const buildFormData = () => {
  const fd = new FormData();
  rawFiles.value.forEach((file) => fd.append('files', file, file.name));
  return fd;
};

let inspectToken = 0;
const inspect = async () => {
  const token = ++inspectToken;
  if (rawFiles.value.length < 1) {
    preview.value = null;
    return;
  }
  inspecting.value = true;
  try {
    const { data } = await axios.post<PainMergePreview>(
      apiUrl('/v3/payouts/pain-files/inspect'),
      buildFormData(),
      { headers: authHeaders() },
    );
    if (token === inspectToken) preview.value = data;
  } catch {
    if (token === inspectToken) preview.value = null;
  } finally {
    if (token === inspectToken) inspecting.value = false;
  }
};

watch(rawFiles, inspect, { deep: false });

const combine = async () => {
  if (!canCombine.value) return;
  merging.value = true;
  try {
    const { data } = await axios.post<Download>(
      apiUrl('/v3/payouts/pain-files/merge'),
      buildFormData(),
      { headers: authHeaders() },
    );
    downloads?.addTask(data);
    notify?.({
      variant: 'success',
      title: t('toolbar.export.mergePainFiles.success'),
    });
    closeModal();
  } catch (e: unknown) {
    const key = (e as { response?: { data?: { error?: { key?: string } } } })?.response?.data?.error?.key;
    if (key === 'mixed_debtor_accounts') {
      notify?.({ variant: 'error', title: t('toolbar.export.mergePainFiles.mixedAccounts') });
    } else if (key === 'invalid_pain_file') {
      notify?.({ variant: 'error', title: t('toolbar.export.mergePainFiles.invalidFiles', { files: '' }) });
    } else {
      notify?.({ variant: 'error', title: t('toolbar.export.mergePainFiles.error') });
    }
  } finally {
    merging.value = false;
  }
};

function closeModal() {
  uploads.value = [];
  preview.value = null;
  internalOpen.value = false;
}
</script>

<style lang="scss" scoped>
// Give the file dropzone / selected-files list more vertical room — the modal is wide and we
// want the list comfortably scrollable when several PAIN files are selected.
.files-area :deep(.drop-box.with-button) {
  height: 320px;
}

.preview {
  @apply rounded-4;
}

.summary-row {
  @apply flex items-center justify-between py-2 border-b border-light-gray;
}

.summary-label {
  @apply text-table-text-gray;
}

.summary-value {
  @apply font-bold;
}

.summary-text {
  @apply mt-3 text-neutral;
}

.error-box {
  @apply rounded-4 bg-red-10 text-red p-4;
}

.warning-box {
  @apply rounded-4 bg-yellow-10 text-dark p-4 mt-4;

  ul {
    @apply list-disc pl-5 mt-2;
  }
}

.warning-title {
  @apply font-bold;
}
</style>
